import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SOURCE_TYPE } from '../constants/Tpiq.constants';
import { TaxplanIQServices } from '../services/TaxplanIQServices';
import { ProjectionSet, ProjectionScenario, ProjectionFieldValue, ProjectionAppliedStrategy, ProjectionStrategyLine } from '../models/projection.model';

@Injectable({
  providedIn: 'root'
})
export class TaxProjectionsService extends TaxplanIQServices {

  constructor(http: HttpClient) {
    super(http);
  }

  private basePath(org: string, userId: string, clientId: string): string {
    return `/TaxPlanningServices/org/${org}/users/${userId}/clients/${clientId}/projections`;
  }

  // ─── Projection Sets ─────────────────────────────────────────────

  getProjectionSets(org: string, userId: string, clientId: string): Observable<ProjectionSet[]> {
    const path = this.basePath(org, userId, clientId);
    return this.query(path, SOURCE_TYPE.JAVA);
  }

  getProjectionSet(org: string, userId: string, clientId: string, setId: string): Observable<ProjectionSet> {
    const path = `${this.basePath(org, userId, clientId)}/${setId}`;
    return this.query(path, SOURCE_TYPE.JAVA);
  }

  createProjectionSet(org: string, userId: string, clientId: string, set: Partial<ProjectionSet>): Observable<ProjectionSet> {
    const path = this.basePath(org, userId, clientId);
    return this.create(path, set, SOURCE_TYPE.JAVA);
  }

  updateProjectionSet(org: string, userId: string, clientId: string, setId: string, set: Partial<ProjectionSet>): Observable<any> {
    const path = `${this.basePath(org, userId, clientId)}/${setId}`;
    return this.update(path, set, SOURCE_TYPE.JAVA);
  }

  deleteProjectionSet(org: string, userId: string, clientId: string, setId: string): Observable<any> {
    const path = `${this.basePath(org, userId, clientId)}/${setId}`;
    return this.delete(path, SOURCE_TYPE.JAVA);
  }

  // ─── Scenarios ───────────────────────────────────────────────────

  addScenario(org: string, userId: string, clientId: string, setId: string, scenario: Partial<ProjectionScenario>): Observable<ProjectionScenario> {
    const path = `${this.basePath(org, userId, clientId)}/${setId}/scenarios`;
    return this.create(path, scenario, SOURCE_TYPE.JAVA);
  }

  updateScenario(org: string, userId: string, clientId: string, setId: string, scenarioId: string, scenario: Partial<ProjectionScenario>): Observable<any> {
    const path = `${this.basePath(org, userId, clientId)}/${setId}/scenarios/${scenarioId}`;
    return this.update(path, scenario, SOURCE_TYPE.JAVA);
  }

  deleteScenario(org: string, userId: string, clientId: string, setId: string, scenarioId: string): Observable<any> {
    const path = `${this.basePath(org, userId, clientId)}/${setId}/scenarios/${scenarioId}`;
    return this.delete(path, SOURCE_TYPE.JAVA);
  }

  // ─── Field Values ────────────────────────────────────────────────

  bulkUpsertFieldValues(org: string, userId: string, clientId: string, setId: string, scenarioId: string, values: ProjectionFieldValue[]): Observable<any> {
    const path = `${this.basePath(org, userId, clientId)}/${setId}/scenarios/${scenarioId}/fields`;
    return this.update(path, values, SOURCE_TYPE.JAVA);
  }

  // ─── OCR Integration ─────────────────────────────────────────────

  getOcrDocuments(org: string, userId: string, clientId: string): Observable<any[]> {
    const path = `${this.basePath(org, userId, clientId)}/ocr-documents`;
    return this.query(path, SOURCE_TYPE.JAVA);
  }

  importOcrData(org: string, userId: string, clientId: string, setId: string, fileId: string): Observable<any> {
    const path = `${this.basePath(org, userId, clientId)}/${setId}/import-ocr/${fileId}`;
    return this.create(path, {}, SOURCE_TYPE.JAVA);
  }

  // ─── Strategy Application ──────────────────────────────────────────

  getStrategyLinesForPlan(org: string, userId: string, clientId: string, setId: string, planId: string): Observable<ProjectionStrategyLine[]> {
    const path = `${this.basePath(org, userId, clientId)}/${setId}/strategy-lines/${planId}`;
    return this.query(path, SOURCE_TYPE.JAVA);
  }

  applyStrategies(org: string, userId: string, clientId: string, setId: string, scenarioId: string, planId: string, strategyLineIds?: string[]): Observable<ProjectionAppliedStrategy[]> {
    const path = `${this.basePath(org, userId, clientId)}/${setId}/scenarios/${scenarioId}/apply-strategies/${planId}`;
    const body = strategyLineIds && strategyLineIds.length > 0 ? { strategyLineIds } : {};
    return this.create(path, body, SOURCE_TYPE.JAVA);
  }

  clearStrategies(org: string, userId: string, clientId: string, setId: string, scenarioId: string): Observable<any> {
    const path = `${this.basePath(org, userId, clientId)}/${setId}/scenarios/${scenarioId}/applied-strategies`;
    return this.delete(path, SOURCE_TYPE.JAVA);
  }
}
